import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { purchaseOrderService } from "~/services/purchasing";
import type {
  Plant,
  PurchaseOrderFilters,
  PurchaseOrderFormData,
  PurchaseOrderStatus,
  ReceptionFormData,
} from "~/types/purchasing";
import { toast } from "sonner";

const QUERY_KEY = "purchase_orders";
const RECEPTIONS_KEY = "purchase_order_receptions";
const OIL_TYPES_KEY = "oil_types";
const RAW_MATERIALS_KEY = "raw_materials";
const PRODUCTS_KEY = "products";

export function usePurchaseOrders(filters?: PurchaseOrderFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => purchaseOrderService.getAll(filters),
  });
}

export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => purchaseOrderService.getById(id!),
    enabled: !!id,
  });
}

export function usePurchaseOrderByNumber(poNumber: number | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, "po_number", poNumber],
    queryFn: () => purchaseOrderService.getByPoNumber(poNumber!),
    enabled: !!poNumber,
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PurchaseOrderFormData) =>
      purchaseOrderService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PurchaseOrderFormData>;
    }) => purchaseOrderService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useUpdatePurchaseOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PurchaseOrderStatus }) =>
      purchaseOrderService.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useMarkAsReceived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrderService.markAsReceived(id),
    onSuccess: (_, id) => {
      // Invalidate PO queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
      // Invalidate stock queries since stock was updated
      queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
    },
  });
}

/**
 * Create a new reception for a purchase order (supports partial receptions with variable costs)
 */
export function useCreateReception() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReceptionFormData) =>
      purchaseOrderService.createReception(data),
    onSuccess: (_, variables) => {
      // Invalidate PO queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.purchase_order_id],
      });
      // Invalidate receptions queries (per-PO history + the global Ingreso ledger)
      queryClient.invalidateQueries({ queryKey: [RECEPTIONS_KEY] });
      // Invalidate stock queries since stock was updated by trigger
      queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
    },
  });
}

/**
 * Get all receptions for a purchase order
 */
export function usePurchaseOrderReceptions(
  purchaseOrderId: string | undefined
) {
  return useQuery({
    queryKey: [RECEPTIONS_KEY, purchaseOrderId],
    queryFn: () => purchaseOrderService.getReceptions(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  });
}

/**
 * Get every reception across all purchase orders (Ingreso ledger).
 */
export function useReceptions() {
  return useQuery({
    queryKey: [RECEPTIONS_KEY, "all"],
    queryFn: () => purchaseOrderService.getAllReceptions(),
  });
}

/**
 * Update a reception item (quantity, cost, expiry date)
 */
export function useUpdateReceptionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      receptionItemId,
      purchaseOrderId,
      data,
    }: {
      receptionItemId: string;
      purchaseOrderId: string;
      data: {
        quantity_received: number;
        actual_unit_cost: number;
        expiry_date?: string | null;
      };
    }) => purchaseOrderService.updateReceptionItem(receptionItemId, data),
    onSuccess: (_, variables) => {
      // Invalidate PO queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.purchaseOrderId],
      });
      // Invalidate receptions queries
      queryClient.invalidateQueries({
        queryKey: [RECEPTIONS_KEY, variables.purchaseOrderId],
      });
      // Invalidate stock queries since stock may have changed
      queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
    },
  });
}

/**
 * Delete a reception item (reverses stock changes)
 */
export function useDeleteReceptionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      receptionItemId,
      purchaseOrderId,
    }: {
      receptionItemId: string;
      purchaseOrderId: string;
    }) => purchaseOrderService.deleteReceptionItem(receptionItemId),
    onSuccess: (_, variables) => {
      // Invalidate PO queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.purchaseOrderId],
      });
      // Invalidate receptions queries
      queryClient.invalidateQueries({
        queryKey: [RECEPTIONS_KEY, variables.purchaseOrderId],
      });
      // Invalidate stock queries since stock was reversed
      queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
    },
  });
}

/**
 * Update the destination plant of a saved reception (moves per-plant stock).
 */
export function useUpdateReceptionPlant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      receptionId,
      newPlant,
    }: {
      receptionId: string;
      purchaseOrderId: string;
      newPlant: Plant;
    }) => purchaseOrderService.updateReceptionPlant(receptionId, newPlant),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.purchaseOrderId],
      });
      queryClient.invalidateQueries({
        queryKey: [RECEPTIONS_KEY, variables.purchaseOrderId],
      });
      queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      toast.success("Planta actualizada");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrderService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrderService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function usePurchaseOrderItems(purchaseOrderId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, purchaseOrderId, "items"],
    queryFn: () => purchaseOrderService.getItems(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  });
}
